import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { createToonGradientMap, toonMat } from './materials.js';

const ENTER_RANGE = 3;
const RIDE_SPEED = 22;

export type ZiplineRoute = {
  ax: number;
  az: number;
  bx: number;
  bz: number;
  height?: number;
};

type ZiplineCable = {
  id: number;
  start: THREE.Vector3;
  end: THREE.Vector3;
  length: number;
  group: THREE.Group;
};

/** Cable rides between POI pairs — thin stylized cylinders with [E] attach. */
export class FpsZiplines {
  private readonly cables: ZiplineCable[] = [];
  private readonly grad = createToonGradientMap();
  private riding: { id: number; fromStart: boolean; t: number } | null = null;
  private nextId = 1;

  constructor(
    private sceneRoot: THREE.Group,
    routes: ZiplineRoute[],
  ) {
    for (const route of routes) {
      this.addCable(route);
    }
  }

  private addCable(route: ZiplineRoute): void {
    const h = route.height ?? 28;
    const start = new THREE.Vector3(route.ax, h, route.az);
    const end = new THREE.Vector3(route.bx, h, route.bz);
    const group = new THREE.Group();
    this.sceneRoot.add(group);

    const dir = new THREE.Vector3().subVectors(end, start);
    const length = dir.length();
    if (length < 2) return;
    dir.normalize();

    const cableMat = toonMat(0x2a3038, this.grad, {
      emissive: 0x0a1828,
      emissiveIntensity: 0.35,
    });
    const cable = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, length, 10), cableMat);
    const mid = start.clone().add(end).multiplyScalar(0.5);
    cable.position.copy(mid);
    cable.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    cable.castShadow = true;
    group.add(cable);

    const poleMat = toonMat(0x4a4030, this.grad, { emissive: 0x1a1008, emissiveIntensity: 0.12 });
    for (const pt of [start, end]) {
      const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.24, h + 1.2, 8), poleMat);
      pole.position.set(pt.x, (h + 1.2) * 0.5 - 0.4, pt.z);
      pole.castShadow = true;
      group.add(pole);
      const wheel = new THREE.Mesh(
        new THREE.TorusGeometry(0.28, 0.06, 8, 16),
        toonMat(0xffcc44, this.grad, { emissive: 0xffaa22, emissiveIntensity: 0.45 }),
      );
      wheel.position.set(pt.x, h, pt.z);
      wheel.rotation.x = Math.PI / 2;
      group.add(wheel);
    }

    this.cables.push({
      id: this.nextId++,
      start,
      end,
      length,
      group,
    });
  }

  /** Nearest attach point within range, if any. */
  getNearAttach(px: number, pz: number): { id: number; fromStart: boolean; label: string } | null {
    if (this.riding) return null;
    let best: { id: number; fromStart: boolean; d: number } | null = null;
    for (const c of this.cables) {
      const ds = Math.hypot(px - c.start.x, pz - c.start.z);
      if (ds < ENTER_RANGE && (!best || ds < best.d)) {
        best = { id: c.id, fromStart: true, d: ds };
      }
      const de = Math.hypot(px - c.end.x, pz - c.end.z);
      if (de < ENTER_RANGE && (!best || de < best.d)) {
        best = { id: c.id, fromStart: false, d: de };
      }
    }
    if (!best) return null;
    return { id: best.id, fromStart: best.fromStart, label: '[E] Ride zipline' };
  }

  tryAttach(px: number, pz: number): boolean {
    const near = this.getNearAttach(px, pz);
    if (!near) return false;
    this.riding = { id: near.id, fromStart: near.fromStart, t: 0 };
    return true;
  }

  isRiding(): boolean {
    return this.riding !== null;
  }

  /** World yaw (radians) along travel direction while riding. */
  getRideYaw(): number | null {
    if (!this.riding) return null;
    const c = this.cables.find((z) => z.id === this.riding!.id);
    if (!c) return null;
    const from = this.riding.fromStart ? c.start : c.end;
    const to = this.riding.fromStart ? c.end : c.start;
    return Math.atan2(to.x - from.x, to.z - from.z);
  }

  /**
   * Slide along cable at 22 m/s. Detach with Space or at end.
   * Returns false when ride ends this frame.
   */
  update(dt: number, body: CANNON.Body, detachRequested: boolean): boolean {
    if (!this.riding) return false;
    const c = this.cables.find((z) => z.id === this.riding!.id);
    if (!c) {
      this.riding = null;
      return false;
    }

    const from = this.riding.fromStart ? c.start : c.end;
    const to = this.riding.fromStart ? c.end : c.start;
    const dirX = to.x - from.x;
    const dirZ = to.z - from.z;
    const dirLen = Math.hypot(dirX, dirZ) || 1;

    this.riding.t += (RIDE_SPEED * dt) / c.length;
    const atEnd = this.riding.t >= 1;
    if (detachRequested || atEnd) {
      const t = Math.min(1, this.riding.t);
      body.position.set(from.x + dirX * t, from.y + (to.y - from.y) * t, from.z + dirZ * t);
      body.velocity.set((dirX / dirLen) * 4.5, -1.2, (dirZ / dirLen) * 4.5);
      this.riding = null;
      return false;
    }

    const t = this.riding.t;
    body.position.set(from.x + dirX * t, from.y + (to.y - from.y) * t, from.z + dirZ * t);
    body.velocity.set((dirX / dirLen) * RIDE_SPEED, 0, (dirZ / dirLen) * RIDE_SPEED);
    return true;
  }

  dispose(): void {
    for (const c of this.cables) {
      this.sceneRoot.remove(c.group);
      c.group.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        const mat = mesh.material;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else if (mat) (mat as THREE.Material).dispose();
      });
    }
    this.cables.length = 0;
    this.riding = null;
  }
}
